import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { I18nProvider } from './contexts/I18nContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import ChatBubble from './components/ChatBubble';

// Pages
import LandingPage from './pages/landing/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import UploadPage from './pages/upload/UploadPage';
import FilesListPage from './pages/files/FilesListPage';
import FileDetailPage from './pages/files/FileDetailPage';
import WalletPage from './pages/wallet/WalletPage';
import ProfilePage from './pages/profile/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import VehiclesPage from './pages/vehicles/VehiclesPage';
import DTCSearchPage from './pages/dtc/DTCSearchPage';
import BoschSearchPage from './pages/bosch/BoschSearchPage';
import KnowledgeBasePage from './pages/kb/KnowledgeBasePage';
import ShopPage from './pages/shop/ShopPage';
import AdminPage from './pages/admin/AdminPage';
import ImpressumPage from './pages/legal/ImpressumPage';
import DatenschutzPage from './pages/legal/DatenschutzPage';
import AGBPage from './pages/legal/AGBPage';
import InvoicesPage from './pages/invoices/InvoicesPage';
import ToolsPage from './pages/tools/ToolsPage';
import SupportPage from './pages/support/SupportPage';
import FileComparePage from './pages/compare/FileComparePage';
import VirtualDynoPage from './pages/dyno/VirtualDynoPage';
import AdminFileSearchPage from './pages/admin/AdminFileSearchPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1D23] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#EC2229] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1D23] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#EC2229] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Placeholder pages for routes not yet implemented
const PlaceholderPage = ({ title }) => (
  <div className="min-h-screen bg-[#1A1D23] flex items-center justify-center">
    <div className="text-center">
      <h1 className="font-heading text-2xl font-bold text-white mb-2">{title}</h1>
      <p className="text-slate-400">Diese Seite wird gerade entwickelt.</p>
    </div>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Legal Pages */}
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route path="/agb" element={<AGBPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><FilesListPage /></ProtectedRoute>} />
      <Route path="/files/:id" element={<ProtectedRoute><FileDetailPage /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/vehicles" element={<ProtectedRoute><VehiclesPage /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
      <Route path="/dtc" element={<ProtectedRoute><DTCSearchPage /></ProtectedRoute>} />
      <Route path="/bosch" element={<ProtectedRoute><BoschSearchPage /></ProtectedRoute>} />
      <Route path="/kb" element={<ProtectedRoute><KnowledgeBasePage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/admin/file-search" element={<ProtectedRoute><AdminFileSearchPage /></ProtectedRoute>} />
      <Route path="/compare" element={<ProtectedRoute><FileComparePage /></ProtectedRoute>} />
      <Route path="/dyno" element={<ProtectedRoute><VirtualDynoPage /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <div className="App dark">
            <AppRoutes />
            <ChatBubble />
            <Toaster position="top-right" richColors />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}

export default App;
